import { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, X, Check, Loader2, Volume2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/utils/api';
import { clearAppCache } from '@/utils/cacheUtils';

export default function VoiceAssistant({ pageContext = 'dashboard' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcribedText, setTranscribedText] = useState('');
  const [parsedResult, setParsedResult] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      // High quality audio settings for better transcription
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 1,
          sampleSize: 16
        } 
      });
      streamRef.current = stream;
      
      // Use higher bitrate for better quality
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processAudio(audioBlob);
      };
      
      // Collect data every 250ms for smoother audio
      mediaRecorder.start(250);
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Microphone access error:', error);
      toast.error('Microphone access denied. Please allow microphone permission.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    setTranscribedText('');
    setParsedResult(null);
    
    // Silently clear cache before processing
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
    } catch (e) {
      // Silent fail - don't show any message
    }
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('page_context', pageContext);
      
      const response = await api.post('/voice/process', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const result = response.data;
      setTranscribedText(result.transcribed_text);
      setParsedResult(result);
      
      if (result.success && result.parsed_data?.created_id) {
        // Clear cache and reload page data
        await clearAppCache();
        toast.success(result.message);
        // Auto close and refresh page after successful creation
        setTimeout(() => {
          resetState();
          setIsOpen(false);
          // Force page refresh to show new data
          window.location.reload();
        }, 1500);
      } else if (result.success) {
        toast.info(result.message);
      } else {
        toast.error(result.message);
      }
      
    } catch (error) {
      console.error('Voice processing error:', error);
      toast.error('Voice processing failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmAction = async () => {
    if (!parsedResult || !parsedResult.parsed_data) return;
    
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('action_type', parsedResult.command_type);
      formData.append('action_data', JSON.stringify(parsedResult.parsed_data));
      
      const response = await api.post('/voice/confirm-action', formData);
      
      if (response.data.success) {
        await clearAppCache();
        toast.success(response.data.message);
        resetState();
        setIsOpen(false);
        // Force page refresh to show new data
        window.location.reload();
      }
    } catch (error) {
      toast.error('Failed to confirm action');
    } finally {
      setIsProcessing(false);
    }
  };

  const resetState = () => {
    setTranscribedText('');
    setParsedResult(null);
    setRecordingTime(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCommandTypeLabel = (type) => {
    const labels = {
      expense: '💰 Expense',
      booking: '📋 Booking',
      customer: '👤 Customer',
      note: '📝 Note',
      unknown: '❓ Unknown'
    };
    return labels[type] || type;
  };

  const getCommandTypeColor = (type) => {
    const colors = {
      expense: 'bg-red-100 text-red-700 border-red-200',
      booking: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      customer: 'bg-purple-100 text-purple-700 border-purple-200',
      note: 'bg-amber-100 text-amber-700 border-amber-200',
      unknown: 'bg-slate-100 text-slate-700 border-slate-200'
    };
    return colors[type] || colors.unknown;
  };

  return (
    <>
      {/* Floating Mic Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-40 w-14 h-14 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-full shadow-lg shadow-emerald-500/30 flex items-center justify-center hover:scale-110 transition-transform duration-200 animate-pulse hover:animate-none"
        data-testid="voice-assistant-btn"
        title="Voice Assistant"
      >
        <Mic size={24} />
      </button>

      {/* Voice Assistant Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              if (!isRecording && !isProcessing) {
                resetState();
                setIsOpen(false);
              }
            }}
          />
          
          {/* Modal Content */}
          <div className="relative w-full sm:w-96 bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-300">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <Volume2 size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold">Voice Assistant</h3>
                    <p className="text-xs text-emerald-100">Ganesa Homestate</p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    if (!isRecording) {
                      resetState();
                      setIsOpen(false);
                    }
                  }}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Recording State */}
              {!transcribedText && !isProcessing && (
                <div className="text-center space-y-4">
                  <p className="text-slate-600 text-sm">
                    {isRecording 
                      ? 'सुन रहा हूं... बोलते रहें'
                      : 'Mic button दबाएं और बोलें'}
                  </p>
                  
                  {/* Mic Button */}
                  <div className="flex justify-center">
                    <button
                      onClick={isRecording ? stopRecording : startRecording}
                      className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isRecording 
                          ? 'bg-red-500 text-white scale-110 animate-pulse shadow-lg shadow-red-500/50' 
                          : 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/30'
                      }`}
                      data-testid="voice-record-btn"
                    >
                      {isRecording ? <MicOff size={32} /> : <Mic size={32} />}
                    </button>
                  </div>
                  
                  {/* Recording Timer */}
                  {isRecording && (
                    <div className="flex items-center justify-center gap-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      <span className="text-lg font-mono text-slate-700">{formatTime(recordingTime)}</span>
                    </div>
                  )}
                  
                  {/* Instructions */}
                  <div className="bg-slate-50 rounded-xl p-3 text-left">
                    <p className="text-xs font-medium text-slate-700 mb-2">🎯 Clear बोलें - Examples:</p>
                    <ul className="text-xs text-slate-500 space-y-1">
                      <li>• "Suresh Kumar ki booking karo 25 lakh"</li>
                      <li>• "Naya customer Amit Sharma mobile 9876543210"</li>
                      <li>• "500 rupaye Rajesh ko office supplies ke liye"</li>
                      <li>• "Note - Kal site visit karna hai"</li>
                    </ul>
                    <p className="text-xs text-amber-600 mt-2 font-medium">💡 Tip: धीरे और साफ़ बोलें</p>
                  </div>
                </div>
              )}

              {/* Processing State */}
              {isProcessing && (
                <div className="text-center py-8 space-y-4">
                  <Loader2 size={48} className="mx-auto text-emerald-500 animate-spin" />
                  <p className="text-slate-600">Processing your voice...</p>
                </div>
              )}

              {/* Result State */}
              {transcribedText && !isProcessing && (
                <div className="space-y-4">
                  {/* Transcribed Text */}
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-500 mb-1">आपने कहा:</p>
                    <p className="text-slate-800 font-medium">"{transcribedText}"</p>
                    {parsedResult?.parsed_data?.corrected_text && parsedResult.parsed_data.corrected_text !== transcribedText && (
                      <p className="text-xs text-emerald-600 mt-1">✓ Corrected: "{parsedResult.parsed_data.corrected_text}"</p>
                    )}
                  </div>
                  
                  {/* Parsed Result */}
                  {parsedResult && (
                    <div className={`rounded-xl p-4 border ${getCommandTypeColor(parsedResult.command_type)}`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-semibold">{getCommandTypeLabel(parsedResult.command_type)}</span>
                        {parsedResult.parsed_data?.created_id && (
                          <span className="text-xs bg-green-500 text-white px-2 py-1 rounded-full">Saved!</span>
                        )}
                      </div>
                      
                      {/* Parsed Data Display */}
                      {parsedResult.command_type === 'expense' && parsedResult.parsed_data && (
                        <div className="space-y-1 text-sm">
                          <p><span className="text-slate-500">Amount:</span> ₹{parsedResult.parsed_data.amount?.toLocaleString('en-IN')}</p>
                          <p><span className="text-slate-500">Category:</span> {parsedResult.parsed_data.category}</p>
                          <p><span className="text-slate-500">Paid By:</span> {parsedResult.parsed_data.paid_by || '-'}</p>
                          <p><span className="text-slate-500">Purpose:</span> {parsedResult.parsed_data.purpose || '-'}</p>
                        </div>
                      )}
                      
                      {parsedResult.command_type === 'customer' && parsedResult.parsed_data && (
                        <div className="space-y-1 text-sm">
                          <p><span className="text-slate-500">Name:</span> {parsedResult.parsed_data.name}</p>
                          <p><span className="text-slate-500">Mobile:</span> {parsedResult.parsed_data.mobile || '-'}</p>
                          <p><span className="text-slate-500">Address:</span> {parsedResult.parsed_data.address || '-'}</p>
                        </div>
                      )}
                      
                      {parsedResult.command_type === 'note' && parsedResult.parsed_data && (
                        <div className="space-y-1 text-sm">
                          <p><span className="text-slate-500">Title:</span> {parsedResult.parsed_data.title}</p>
                          <p><span className="text-slate-500">Content:</span> {parsedResult.parsed_data.content}</p>
                        </div>
                      )}
                      
                      {parsedResult.command_type === 'booking' && parsedResult.parsed_data && (
                        <div className="space-y-1 text-sm">
                          <p><span className="text-slate-500">Customer:</span> {parsedResult.parsed_data.customer_name}</p>
                          <p><span className="text-slate-500">Mobile:</span> {parsedResult.parsed_data.mobile || '-'}</p>
                          <p><span className="text-slate-500">Property:</span> {parsedResult.parsed_data.property_name || '-'}</p>
                          <p><span className="text-slate-500">Amount:</span> ₹{parsedResult.parsed_data.amount?.toLocaleString('en-IN')}</p>
                          <p><span className="text-slate-500">Payment:</span> {parsedResult.parsed_data.payment_type || 'Full Payment'}</p>
                        </div>
                      )}
                      
                      {parsedResult.command_type === 'unknown' && (
                        <div className="flex items-center gap-2 text-sm">
                          <AlertCircle size={16} />
                          <span>{parsedResult.message}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={resetState}
                      className="flex-1"
                    >
                      <Mic size={18} className="mr-2" />
                      Try Again
                    </Button>
                    
                    {parsedResult && !parsedResult.parsed_data?.created_id && parsedResult.command_type !== 'unknown' && (
                      <Button
                        onClick={confirmAction}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Check size={18} className="mr-2" />
                        Confirm
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
